import React from 'react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { usePOS } from '../hooks/usePOS';
import {
  Beer, Wine, GlassWater, Martini, Play, Square, DollarSign,
  CreditCard, Receipt, Plus, Minus, Trash2, History,
  PackagePlus, ArrowLeft
} from 'lucide-react';

const POS = () => {
  const pos = usePOS();

  if (pos.loading) {
    return (
      <div className="min-h-screen bg-[#f5f5dc] flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#1e3a5f]"></div>
      </div>
    );
  }

  if (pos.view === 'sessions') return <><OfflineBanner pos={pos} /><SessionsView pos={pos} /></>;
  if (pos.view === 'report' && pos.sessionReport) return <><OfflineBanner pos={pos} /><ReportView pos={pos} /></>;
  return <><OfflineBanner pos={pos} /><POSView pos={pos} /></>;
};

/* Top banner showing online/offline state + pending offline-queue size. */
const OfflineBanner = ({ pos }) => {
  const { isOnline, pendingQueue = [], syncing, syncPendingQueue } = pos;
  if (isOnline && pendingQueue.length === 0) return null;

  const downloadBackup = () => {
    try {
      const blob = new Blob([JSON.stringify(pendingQueue, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `pos-offline-queue-${new Date().toISOString().slice(0, 19).replace(/[:T]/g, '-')}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (e) { /* best-effort */ }
  };

  return (
    <div
      className={`w-full text-sm px-4 py-2 flex items-center justify-between gap-3 ${
        isOnline ? 'bg-amber-100 text-amber-900 border-b border-amber-300' : 'bg-red-100 text-red-900 border-b border-red-300'
      }`}
      data-testid="offline-banner"
    >
      <div className="flex items-center gap-2 flex-wrap">
        <span className={`inline-block w-2 h-2 rounded-full ${isOnline ? 'bg-amber-600' : 'bg-red-600 animate-pulse'}`} />
        <span className="font-semibold">
          {isOnline ? 'Online' : 'OFFLINE'}
        </span>
        {pendingQueue.length > 0 && (
          <span data-testid="pending-queue-count">
            · {pendingQueue.length} transaction{pendingQueue.length === 1 ? '' : 's'} queued
            {syncing && ' (syncing…)'}
          </span>
        )}
        {!isOnline && (
          <span className="ml-2 text-xs italic">Sales saved to this device — will sync automatically when you reconnect. Do NOT clear the browser cache.</span>
        )}
      </div>
      <div className="flex items-center gap-3 flex-shrink-0">
        {pendingQueue.length > 0 && (
          <button
            onClick={downloadBackup}
            className="text-xs underline font-semibold hover:no-underline"
            data-testid="export-queue-btn"
            title="Download a JSON backup of all queued sales (recommended before reconnecting)"
          >
            Backup queue
          </button>
        )}
        {isOnline && pendingQueue.length > 0 && !syncing && (
          <button
            onClick={syncPendingQueue}
            className="text-xs underline font-semibold hover:no-underline"
            data-testid="manual-sync-btn"
          >
            Sync now
          </button>
        )}
      </div>
    </div>
  );
};

// --- Sessions View ---
const SessionsView = ({ pos }) => (
  <div className="min-h-screen bg-[#f5f5dc] p-6">
    <div className="max-w-6xl mx-auto">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-3xl font-bold text-[#1e3a5f]">Cocktail Coach POS</h1>
          <p className="text-gray-600">Point of Sale System</p>
        </div>
        <div className="flex items-center gap-2">
          <Button asChild variant="outline" data-testid="pos-manage-menu-btn">
            <a href="/settings/pos-menu"><Wine className="h-4 w-4 mr-2" />Manage Menu</a>
          </Button>
          <Button onClick={() => pos.setShowNewSession(true)} className="bg-[#1e3a5f] hover:bg-[#2d4a6f]" data-testid="new-session-btn">
            <Play className="h-4 w-4 mr-2" /> New Session
          </Button>
        </div>
      </div>

      {pos.showNewSession && <NewSessionCard pos={pos} />}

      <Card className="mb-6">
        <CardHeader><CardTitle className="flex items-center gap-2"><Play className="h-5 w-5 text-green-600" /> Active Sessions</CardTitle></CardHeader>
        <CardContent>
          {pos.sessions.filter(s => s.is_active).length === 0 ? (
            <p className="text-gray-500">No active sessions</p>
          ) : (
            <div className="grid gap-4">
              {pos.sessions.filter(s => s.is_active).map(session => (
                <div key={session.id} className="flex items-center justify-between p-4 bg-green-50 rounded-lg border border-green-200">
                  <div>
                    <h3 className="font-semibold">{session.event_name}</h3>
                    <p className="text-sm text-gray-600">{session.event_date}</p>
                    <div className="flex gap-4 mt-2 text-sm">
                      <span>Sales: ${session.total_sales?.toFixed(2) || '0.00'}</span>
                      <span>Tips: ${session.total_tips?.toFixed(2) || '0.00'}</span>
                      <span>Transactions: {session.total_transactions || 0}</span>
                    </div>
                  </div>
                  <Button onClick={() => pos.handleResumeSession(session)} className="bg-[#1e3a5f] hover:bg-[#2d4a6f]" data-testid={`resume-session-${session.id}`}>Resume</Button>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle className="flex items-center gap-2"><History className="h-5 w-5" /> Past Sessions</CardTitle></CardHeader>
        <CardContent>
          {pos.sessions.filter(s => !s.is_active).length === 0 ? (
            <p className="text-gray-500">No past sessions</p>
          ) : (
            <div className="grid gap-4">
              {pos.sessions.filter(s => !s.is_active).map(session => (
                <div key={session.id} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg border">
                  <div>
                    <h3 className="font-semibold">{session.event_name}</h3>
                    <p className="text-sm text-gray-600">{session.event_date}</p>
                    <div className="flex gap-4 mt-2 text-sm">
                      <span>Total Sales: ${session.total_sales?.toFixed(2) || '0.00'}</span>
                      <span>Total Tips: ${session.total_tips?.toFixed(2) || '0.00'}</span>
                    </div>
                  </div>
                  <Button variant="outline" onClick={() => pos.handleViewReport(session)} data-testid={`view-report-${session.id}`}>
                    <Receipt className="h-4 w-4 mr-2" /> View Report
                  </Button>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  </div>
);

const NewSessionCard = ({ pos }) => (
  <Card className="mb-6 border-2 border-[#c9a227]">
    <CardHeader><CardTitle>Start New POS Session</CardTitle></CardHeader>
    <CardContent className="space-y-4">
      <div>
        <Label>Select Event</Label>
        <Select value={pos.selectedContract} onValueChange={pos.setSelectedContract}>
          <SelectTrigger data-testid="contract-select"><SelectValue placeholder="Choose an event..." /></SelectTrigger>
          <SelectContent>
            {pos.contracts.filter(c => c.event_type === 'cocktail_coach' || c.service_type === 'cocktail_coach').map(contract => (
              <SelectItem key={contract.id} value={contract.id}>{contract.event_name} - {contract.event_date}</SelectItem>
            ))}
            {pos.contracts.filter(c => c.event_type !== 'cocktail_coach' && c.service_type !== 'cocktail_coach').map(contract => (
              <SelectItem key={contract.id} value={contract.id}>{contract.event_name} - {contract.event_date}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div>
        <Label>Starting Inventory (Optional)</Label>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mt-2">
          {pos.drinkMenu.map(drink => (
            <div key={drink.id} className="flex items-center gap-2">
              <Input type="number" min="0" placeholder="0" className="w-20"
                onChange={(e) => pos.setStartingInventory({ ...pos.startingInventory, [drink.id]: parseInt(e.target.value) || 0 })} />
              <span className="text-sm text-gray-600">{drink.name}</span>
            </div>
          ))}
        </div>
      </div>
      <div className="flex gap-2">
        <Button onClick={pos.handleStartSession} className="bg-green-600 hover:bg-green-700" data-testid="start-session-btn">
          <Play className="h-4 w-4 mr-2" /> Start Session
        </Button>
        <Button variant="outline" onClick={() => pos.setShowNewSession(false)}>Cancel</Button>
      </div>
    </CardContent>
  </Card>
);

// --- Z-Report (printable end-of-night) ---
const ReportView = ({ pos }) => {
  const session = pos.sessionReport.session;
  const totals = pos.sessionReport.totals || {};
  const breakdown = pos.sessionReport.payment_breakdown || {};
  // Tolerate both legacy shape (number) and new shape ({total, tips, count})
  const pm = (key) => {
    const v = breakdown[key];
    if (v == null) return { total: 0, tips: 0, count: 0 };
    if (typeof v === 'number') return { total: v, tips: 0, count: 0 };
    return { total: +v.total || 0, tips: +v.tips || 0, count: +v.count || 0 };
  };
  const methods = [
    ['cash', 'Cash'],
    ['visa', 'Visa'],
    ['mastercard', 'Mastercard'],
    ['amex', 'Amex'],
    ['debit', 'Debit'],
  ];
  const handlePrintZReport = () => {
    // Open the z-report in a new window — same reason as InvoicePreviewModal:
    // robust across browsers and bypasses any modal / transform interference.
    const el = document.querySelector('.z-report-printable');
    if (!el) return;
    const w = window.open('', '_blank', 'width=900,height=1100');
    if (!w) { alert('Pop-up blocked. Please allow pop-ups for this site to print.'); return; }
    const styleLinks = Array.from(document.querySelectorAll('link[rel="stylesheet"]'))
      .map((l) => l.outerHTML).join('\n');
    const styleTags = Array.from(document.querySelectorAll('style'))
      .map((s) => `<style>${s.innerHTML}</style>`).join('\n');
    w.document.open();
    w.document.write(`<!doctype html><html><head><meta charset="utf-8"><title>Z-Report</title>${styleLinks}${styleTags}
      <style>
        @page { size: letter; margin: 0.4in; }
        body { background: #fff; margin: 0; font-family: ui-sans-serif, system-ui, -apple-system, "Segoe UI", Roboto, sans-serif; }
      </style></head><body>${el.outerHTML}</body></html>`);
    w.document.close();
    w.onload = () => { w.focus(); setTimeout(() => { w.print(); w.close(); }, 200); };
  };
  const sessionEnded = session.ended_at || session.updated_at || new Date().toISOString();
  return (
    <div className="min-h-screen bg-[#f5f5dc] p-6">
      <div className="max-w-4xl mx-auto">
        <div className="flex items-center justify-between mb-4 no-print">
          <Button variant="outline" onClick={() => { pos.setView('sessions'); pos.setSessionReport(null); }} data-testid="back-to-sessions">
            <ArrowLeft className="h-4 w-4 mr-2" /> Back to Sessions
          </Button>
          <Button onClick={handlePrintZReport} className="bg-[#1e3a5f] hover:bg-[#2d5016]" data-testid="print-z-report-btn">
            <Receipt className="h-4 w-4 mr-2" /> Print Z-Report
          </Button>
        </div>

        <Card className="mb-6 z-report-printable">
          <CardHeader className="bg-[#1e3a5f] text-white rounded-t-lg">
            <CardTitle className="text-2xl">Z-Report · End of Night</CardTitle>
            <p className="text-sm opacity-90">
              {session.event_name} · {session.event_date} · Closed {new Date(sessionEnded).toLocaleString()}
            </p>
          </CardHeader>
          <CardContent className="p-6 space-y-6">
            {/* Tax Reconciliation block */}
            <div>
              <h3 className="text-sm font-bold text-[#1e3a5f] uppercase tracking-wider mb-2">Tax Reconciliation (CRA)</h3>
              <table className="w-full text-sm" data-testid="z-report-tax-table">
                <tbody>
                  <tr className="border-b"><td className="py-1.5 text-gray-700">Subtotal (Pre-Tax)</td><td className="py-1.5 text-right font-mono">${(totals.subtotal_pre_tax || 0).toFixed(2)}</td></tr>
                  <tr className="border-b"><td className="py-1.5 text-gray-700">HST Collected (13%)</td><td className="py-1.5 text-right font-mono">${(totals.hst_collected || 0).toFixed(2)}</td></tr>
                  <tr className="border-b font-semibold"><td className="py-1.5 text-[#1e3a5f]">Total Sales (Incl. Tax)</td><td className="py-1.5 text-right font-mono">${(totals.total_sales || 0).toFixed(2)}</td></tr>
                  <tr className="border-b"><td className="py-1.5 text-gray-700">Tips Paid Out</td><td className="py-1.5 text-right font-mono">${(totals.total_tips || 0).toFixed(2)}</td></tr>
                  <tr className="bg-[#1e3a5f] text-white"><td className="py-2 px-2 font-bold">GRAND TOTAL</td><td className="py-2 px-2 text-right font-mono font-bold">${(totals.grand_total || 0).toFixed(2)}</td></tr>
                </tbody>
              </table>
            </div>

            {/* Payment Type Breakdown */}
            <div>
              <h3 className="text-sm font-bold text-[#1e3a5f] uppercase tracking-wider mb-2">Totals by Payment Type</h3>
              <table className="w-full text-sm" data-testid="z-report-payment-table">
                <thead className="bg-gray-50 border-b-2 border-[#1e3a5f]">
                  <tr>
                    <th className="text-left py-1.5 px-2 font-semibold text-[#1e3a5f]">Method</th>
                    <th className="text-right py-1.5 px-2 font-semibold text-[#1e3a5f] w-20">Txns</th>
                    <th className="text-right py-1.5 px-2 font-semibold text-[#1e3a5f] w-28">Sales</th>
                    <th className="text-right py-1.5 px-2 font-semibold text-[#1e3a5f] w-28">Tips</th>
                    <th className="text-right py-1.5 px-2 font-semibold text-[#1e3a5f] w-28">Total</th>
                  </tr>
                </thead>
                <tbody>
                  {methods.map(([key, label]) => {
                    const r = pm(key);
                    return (
                      <tr key={key} className="border-b" data-testid={`z-report-row-${key}`}>
                        <td className="py-1.5 px-2">{label}</td>
                        <td className="py-1.5 px-2 text-right font-mono">{r.count}</td>
                        <td className="py-1.5 px-2 text-right font-mono">${(r.total - r.tips).toFixed(2)}</td>
                        <td className="py-1.5 px-2 text-right font-mono">${r.tips.toFixed(2)}</td>
                        <td className="py-1.5 px-2 text-right font-mono font-semibold">${r.total.toFixed(2)}</td>
                      </tr>
                    );
                  })}
                  <tr className="bg-gray-100 border-t-2 border-[#1e3a5f] font-bold">
                    <td className="py-1.5 px-2">TOTAL</td>
                    <td className="py-1.5 px-2 text-right font-mono">{methods.reduce((s, [k]) => s + pm(k).count, 0)}</td>
                    <td className="py-1.5 px-2 text-right font-mono">${methods.reduce((s, [k]) => { const r = pm(k); return s + (r.total - r.tips); }, 0).toFixed(2)}</td>
                    <td className="py-1.5 px-2 text-right font-mono">${methods.reduce((s, [k]) => s + pm(k).tips, 0).toFixed(2)}</td>
                    <td className="py-1.5 px-2 text-right font-mono">${methods.reduce((s, [k]) => s + pm(k).total, 0).toFixed(2)}</td>
                  </tr>
                </tbody>
              </table>
            </div>

            {/* Sales by Item */}
            <div>
              <h3 className="text-sm font-bold text-[#1e3a5f] uppercase tracking-wider mb-2">Sales by Item</h3>
              <div className="space-y-1">
                {Object.entries(pos.sessionReport.sales_by_drink).map(([id, data]) => (
                  <div key={id} className="flex justify-between text-sm py-1 px-2 border-b">
                    <span>{data.name}</span>
                    <span><span className="text-gray-500 mr-3">{data.quantity_sold} sold</span><span className="font-mono">${(data.total_sales || 0).toFixed(2)}</span></span>
                  </div>
                ))}
              </div>
            </div>

            {/* Inventory Variance (kept from original) */}
            {Object.keys(pos.sessionReport.inventory_variance).length > 0 && (
              <div>
                <h3 className="text-sm font-bold text-[#1e3a5f] uppercase tracking-wider mb-2">Inventory Variance</h3>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-100">
                      <tr><th className="p-2 text-left">Item</th><th className="p-2">Starting</th><th className="p-2">Sold</th><th className="p-2">Expected</th><th className="p-2">Actual</th><th className="p-2">Variance</th></tr>
                    </thead>
                    <tbody>
                      {Object.entries(pos.sessionReport.inventory_variance).map(([id, data]) => (
                        <tr key={id} className="border-b">
                          <td className="p-2">{data.name}</td>
                          <td className="p-2 text-center">{data.starting}</td>
                          <td className="p-2 text-center">{data.sold}</td>
                          <td className="p-2 text-center">{data.expected_ending}</td>
                          <td className="p-2 text-center">{data.actual_ending}</td>
                          <td className={`p-2 text-center font-semibold ${data.variance !== 0 ? 'text-red-600' : 'text-green-600'}`}>
                            {data.variance > 0 ? '+' : ''}{data.variance}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Sign-off lines (printable) */}
            <div className="grid grid-cols-2 gap-8 pt-6 border-t-2 border-dashed border-gray-300 text-sm">
              <div>
                <div className="border-b border-gray-400 h-6" />
                <p className="text-xs text-gray-600 mt-1">Closed by / Signature</p>
              </div>
              <div>
                <div className="border-b border-gray-400 h-6" />
                <p className="text-xs text-gray-600 mt-1">Date</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Print-only CSS — when printing-z-report class is on body, hide everything except the report card */}
        <style>{`
          @media print {
            body.printing-z-report * { visibility: hidden; }
            body.printing-z-report .z-report-printable,
            body.printing-z-report .z-report-printable * { visibility: visible; }
            body.printing-z-report .z-report-printable { position: absolute; top: 0; left: 0; width: 100%; }
            .no-print { display: none !important; }
          }
        `}</style>
      </div>
    </div>
  );
};

// --- POS View (Active Session) ---
const POSView = ({ pos }) => (
  <div className="min-h-screen bg-[#1e3a5f] p-2 md:p-4">
    <POSHeader pos={pos} />
    {pos.showRestockModal && <RestockModal pos={pos} />}

    <div className={`grid gap-4 ${pos.isMobileView ? 'grid-cols-1' : 'grid-cols-12'}`}>
      <div className={pos.isMobileView ? '' : 'col-span-8'}>
        <DrinkCategory title="Beer" icon={Beer} category="beer" pos={pos} />
        <DrinkCategory title="Wine" icon={Wine} category="wine" pos={pos} />
        <DrinkCategory title="Spirits & Cocktails" icon={Martini} categories={['spirits', 'cocktails']} pos={pos} />
        <DrinkCategory title="Non-Alcoholic" icon={GlassWater} category="non_alcoholic" pos={pos} />
      </div>
      <div className={pos.isMobileView ? '' : 'col-span-4'}>
        <CartPanel pos={pos} />
      </div>
    </div>

    {pos.transactions.length > 0 && <RecentTransactions pos={pos} />}
  </div>
);

const POSHeader = ({ pos }) => (
  <div className="flex flex-wrap justify-between items-center mb-4 text-white gap-2">
    <div className="flex-1 min-w-0">
      <h1 className="text-lg md:text-2xl font-bold truncate">{pos.activeSession?.event_name}</h1>
      <p className="text-xs md:text-sm opacity-80">{pos.activeSession?.event_date}</p>
    </div>
    <div className="flex items-center gap-2 md:gap-4 flex-wrap">
      <div className="text-right">
        <p className="text-xs md:text-sm opacity-80">Session Total</p>
        <p className="text-lg md:text-xl font-bold">${((pos.activeSession?.total_sales || 0) + (pos.activeSession?.total_tips || 0)).toFixed(2)}</p>
      </div>
      <Button onClick={() => pos.setShowRestockModal(true)} className="bg-blue-600 hover:bg-blue-700" data-testid="restock-btn">
        <PackagePlus className="h-4 w-4 md:mr-2" /><span className="hidden md:inline">Restock</span>
      </Button>
      <Button variant="destructive" onClick={() => {
        pos.setEndingInventory({ ...pos.activeSession?.starting_inventory });
        if (window.confirm('Close this session? You will need to enter ending inventory.')) pos.handleCloseSession();
      }} data-testid="close-session-btn">
        <Square className="h-4 w-4 md:mr-2" /><span className="hidden md:inline">Close Session</span>
      </Button>
      <Button variant="outline" onClick={() => pos.setView('sessions')} className="text-white border-white hover:bg-white/10">
        <ArrowLeft className="h-4 w-4" />
      </Button>
    </div>
  </div>
);

const DrinkCategory = ({ title, icon: Icon, category, categories, pos }) => {
  const drinks = categories
    ? pos.drinkMenu.filter(d => categories.includes(d.category))
    : pos.drinkMenu.filter(d => d.category === category);
  if (drinks.length === 0) return null;
  return (
    <div className="mb-4">
      <h3 className="text-white font-semibold mb-2 flex items-center gap-2"><Icon className="h-5 w-5" /> {title}</h3>
      <div className={`grid gap-2 ${pos.isMobileView ? 'grid-cols-2' : 'grid-cols-4'}`}>
        {drinks.map(drink => (
          <Button key={drink.id} onClick={() => pos.addToCart(drink)}
            className={`h-16 md:h-20 flex flex-col items-center justify-center ${pos.getCategoryColor(drink.category)}`}
            data-testid={`drink-btn-${drink.id}`}>
            <span className="text-xs md:text-sm font-medium text-center leading-tight">{drink.name}</span>
            <span className="text-base md:text-lg font-bold">${drink.default_price}</span>
          </Button>
        ))}
      </div>
    </div>
  );
};

const CartPanel = ({ pos }) => (
  <Card className={`flex flex-col ${pos.isMobileView ? 'fixed bottom-0 left-0 right-0 max-h-[50vh] rounded-b-none shadow-2xl z-40' : 'h-full'}`}>
    <CardHeader className="pb-2"><CardTitle className="text-lg">Current Sale</CardTitle></CardHeader>
    <CardContent className="flex-1 flex flex-col overflow-hidden">
      <div className="flex-1 overflow-auto space-y-2 mb-4 max-h-[300px]">
        {pos.cart.length === 0 ? (
          <p className="text-gray-400 text-center py-8">No items in cart</p>
        ) : pos.cart.map(item => (
          <div key={item.drink_id} className="flex items-center justify-between p-2 bg-gray-50 rounded">
            <div className="flex-1">
              <p className="font-medium text-sm">{item.name}</p>
              <p className="text-xs text-gray-500">${item.price} x {item.quantity}</p>
            </div>
            <div className="flex items-center gap-1">
              <Button size="sm" variant="outline" className="h-7 w-7 p-0" onClick={() => pos.updateCartQuantity(item.drink_id, -1)}><Minus className="h-3 w-3" /></Button>
              <span className="w-6 text-center">{item.quantity}</span>
              <Button size="sm" variant="outline" className="h-7 w-7 p-0" onClick={() => pos.updateCartQuantity(item.drink_id, 1)}><Plus className="h-3 w-3" /></Button>
              <Button size="sm" variant="destructive" className="h-7 w-7 p-0 ml-1" onClick={() => pos.removeFromCart(item.drink_id)}><Trash2 className="h-3 w-3" /></Button>
            </div>
          </div>
        ))}
      </div>
      <div className="mb-3">
        <Label className="text-sm">Payment Method</Label>
        <div className="grid grid-cols-3 gap-2 mt-1">
          {['cash', 'visa', 'mastercard', 'amex', 'debit'].map(method => (
            <Button key={method} variant={pos.paymentMethod === method ? 'default' : 'outline'} size="sm"
              onClick={() => pos.setPaymentMethod(method)}
              className={pos.paymentMethod === method ? 'bg-[#1e3a5f]' : ''}
              data-testid={`payment-${method}`}>
              {method === 'cash' ? <DollarSign className="h-3 w-3 mr-1" /> : <CreditCard className="h-3 w-3 mr-1" />}
              <span className="capitalize text-xs">{method}</span>
            </Button>
          ))}
        </div>
      </div>
      <div className="mb-3">
        <Label className="text-sm">Amount Received</Label>
        <div className="relative">
          <DollarSign className="absolute left-2 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          <Input
            type="number" step="0.01" min="0" placeholder="0.00"
            value={pos.amountReceived}
            onChange={(e) => pos.setAmountReceived(e.target.value)}
            className="pl-8"
            data-testid="amount-received-input"
          />
        </div>
        <p className="text-xs text-gray-500 mt-1">Tip is calculated as Amount Received − Total.</p>
      </div>
      <div className="border-t pt-3 mb-3">
        <div className="flex justify-between text-sm"><span>Subtotal (pre-tax):</span><span data-testid="receipt-subtotal-pretax">${pos.cartSubtotalPreTax.toFixed(2)}</span></div>
        <div className="flex justify-between text-sm"><span>HST (13%):</span><span data-testid="receipt-tax">${pos.cartTaxAmount.toFixed(2)}</span></div>
        <div className="flex justify-between text-sm font-semibold"><span>Total:</span><span data-testid="receipt-total">${pos.cartTotalPostTax.toFixed(2)}</span></div>
        {(parseFloat(pos.amountReceived) || 0) > 0 && (
          <>
            <div className="flex justify-between text-xs text-gray-500 mt-2"><span>Amount Received:</span><span data-testid="receipt-amount-received">${(parseFloat(pos.amountReceived) || 0).toFixed(2)}</span></div>
            <div className="flex justify-between text-sm"><span>Tip:</span><span data-testid="receipt-tip">${pos.tipFromCash.toFixed(2)}</span></div>
          </>
        )}
        <div className="flex justify-between font-bold text-lg mt-1 border-t pt-1"><span>Grand Total:</span><span className="text-green-600" data-testid="receipt-grand-total">${pos.grandTotal.toFixed(2)}</span></div>
      </div>
      <Button onClick={pos.handleProcessSale} disabled={pos.cart.length === 0} className="w-full h-14 text-lg bg-green-600 hover:bg-green-700" data-testid="process-sale-btn">
        <Receipt className="h-5 w-5 mr-2" /> Process Sale
      </Button>
    </CardContent>
  </Card>
);

const RestockModal = ({ pos }) => (
  <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
    <Card className="w-full max-w-lg max-h-[80vh] overflow-y-auto">
      <CardHeader className="bg-blue-600 text-white">
        <CardTitle className="flex items-center gap-2"><PackagePlus className="h-5 w-5" /> Add Inventory</CardTitle>
      </CardHeader>
      <CardContent className="p-4">
        <p className="text-sm text-gray-600 mb-4">Enter quantities to add to current inventory:</p>
        <div className="space-y-3 max-h-[400px] overflow-y-auto">
          {pos.drinkMenu.map(drink => (
            <div key={drink.id} className="flex items-center justify-between p-2 bg-gray-50 rounded">
              <div>
                <p className="font-medium text-sm">{drink.name}</p>
                <p className="text-xs text-gray-500">Current: {pos.activeSession?.starting_inventory?.[drink.id] || 0}</p>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-500">Add:</span>
                <Input type="number" min="0" placeholder="0" className="w-20 text-center"
                  value={pos.restockAmounts[drink.id] || ''}
                  onChange={(e) => pos.setRestockAmounts({ ...pos.restockAmounts, [drink.id]: parseInt(e.target.value) || 0 })}
                  data-testid={`restock-input-${drink.id}`} />
              </div>
            </div>
          ))}
        </div>
        <div className="flex justify-end gap-2 mt-4 pt-4 border-t">
          <Button variant="outline" onClick={() => { pos.setShowRestockModal(false); pos.setRestockAmounts({}); }}>Cancel</Button>
          <Button onClick={pos.handleRestock} className="bg-blue-600 hover:bg-blue-700" data-testid="confirm-restock-btn">
            <Plus className="h-4 w-4 mr-2" /> Add to Inventory
          </Button>
        </div>
      </CardContent>
    </Card>
  </div>
);

const RecentTransactions = ({ pos }) => (
  <Card className="mt-4">
    <CardHeader className="py-3">
      <CardTitle className="text-sm flex items-center gap-2"><History className="h-4 w-4" /> Recent Transactions</CardTitle>
    </CardHeader>
    <CardContent className="py-2">
      <div className="flex gap-2 overflow-x-auto pb-2">
        {pos.transactions.slice(0, 10).map(tx => (
          <div key={tx.id} className="flex-shrink-0 p-2 bg-gray-50 rounded-lg min-w-[150px]">
            <div className="flex justify-between items-start mb-1">
              <Badge variant="outline" className="text-xs capitalize">{tx.payment_method}</Badge>
              <Button size="sm" variant="ghost" className="h-5 w-5 p-0 text-red-500 hover:text-red-700" onClick={() => pos.handleVoidTransaction(tx.id)}>
                <Trash2 className="h-3 w-3" />
              </Button>
            </div>
            <p className="font-semibold">${tx.total?.toFixed(2)}</p>
            <p className="text-xs text-gray-500">{tx.items?.length} item(s)</p>
          </div>
        ))}
      </div>
    </CardContent>
  </Card>
);

export default POS;
